import { IBusLocation } from "@/domain/entities/lineInfo";
import { AllocStatusCode, BusLocationMarker, BusStopEventCode } from "@/types";
import { ref } from "vue";

interface CreateBusLocationMarkerParams {
  naverMap: any;
  lat: number;
  lng: number;
  zIndex: number;
  plateNumber: string;
  nextStopName: string;
  bearing?: number;
}

export const useBusLocationMarkers = () => {
  const busLocationMarkers = ref<BusLocationMarker[]>([]);

  const createBusLocationMarkers = (
    naverMap: any,
    busLocations: IBusLocation[],
    shouldShowNextStopName = false
  ) => {
    if (!busLocations.length) return;

    // 현재 운행중인 버스만 필터링
    const filteredBusLocations = busLocations.filter(
      (busLocation) => !hasBusEndedDrivingOrArrivedLastStop(busLocation)
    );

    filteredBusLocations.forEach((busLocation) => {
      const { lat, lng, plateNumber } = busLocation;
      const slicedPlateNumber = slicePlateNumber(plateNumber);
      const marker = createBusLocationMarker({
        naverMap,
        lat,
        lng,
        zIndex: 100,
        plateNumber: slicedPlateNumber,
        bearing: busLocation.bearing,
        nextStopName: shouldShowNextStopName ? busLocation.nextStopName : "",
      });

      busLocationMarkers.value.push({
        busLocation,
        marker,
      });

      marker.setMap(naverMap);
    });
  };

  const createBusLocationMarker = ({
    naverMap,
    lat,
    lng,
    zIndex,
    plateNumber,
    bearing = -1,
    nextStopName,
  }: CreateBusLocationMarkerParams) => {
    const position = new window.naver.maps.LatLng(lat, lng);
    const busIconUrl = require("@/assets/icons/bus_circle_blue.svg");
    const arrowIconUrl = require("@/assets/icons/arrow-top.svg");
    const arrowStyle =
      bearing === undefined || bearing === -1
        ? "display: none;"
        : `
      position: absolute;
      top: 0;
      left: 50%;
        transform: translate(-50%, 0) rotate(${bearing}deg);
      transform-origin: center center;
    `;

    const contentHtmlStr = `
        <div style="display: flex; flex-direction: column; justify-content: center; transform: translate(-50%, -50%);">
          <div style="position: relative">
            <img src="${busIconUrl}" alt="버스 아이콘" />
            <img src="${arrowIconUrl}" alt="방향 아이콘" style="${arrowStyle}" />
          </div>
          <div
          style="
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
            border-radius: 2px;
            background-color: #000000b3;"
          >
            <span style="
              color: #fff;
              font-size: 13px;
              font-weight: bold;
            ">
            ${plateNumber}
            </span>
          </div>
          ${
            nextStopName
              ? `<p style="
              position: absolute;
              left: 50%;
              bottom: -4px;
              transform: translate(-50%, 100%);
              padding: 4px;
              border-radius: 2px;
              font-size: 12px;
              font-weight: bold;
              color: #fff;
              background-color: #000000b3;
              white-space: nowrap;
              "
            >다음: ${nextStopName}</p>`
              : ""
          }
        </div>`;

    const markerOptions = {
      position,
      naverMap,
      icon: {
        content: contentHtmlStr,
      },
      zIndex: zIndex,
    };

    return new window.naver.maps.Marker(markerOptions);
  };

  const removeBusLocationMarkers = () => {
    busLocationMarkers.value.forEach((marker) => {
      marker.marker.setMap(null);
    });

    busLocationMarkers.value = [];
  };

  return {
    busLocationMarkers,
    createBusLocationMarker,
    createBusLocationMarkers,
    removeBusLocationMarkers,
  };
};

/** 차번호 뒤에 4자리 가져오기 ex) 123서울1234 => 1234 */
function slicePlateNumber(plateNumber: string) {
  if (plateNumber.length > 4) return plateNumber.slice(-4);

  return plateNumber;
}

/** 버스 운행 종료 여부 판단하는 함수 */
const hasBusEndedDrivingOrArrivedLastStop = (location: IBusLocation) => {
  const lastBusStop = location.busStopEvents[location.busStopEvents.length - 1];

  return (
    location.allocStatusCode === AllocStatusCode.DRIVEN ||
    lastBusStop.busStopEventCode === BusStopEventCode.LastLeave
  );
};
